const ProgressEvent =
  globalThis.ProgressEvent ||
  class ProgressEvent extends Event implements globalThis.ProgressEvent {
    loaded = 0;
    total = 0;
    lengthComputable = false;

    constructor(type: string, eventInitDict: ProgressEventInit) {
      super(type, eventInitDict);
      if (eventInitDict?.loaded !== undefined) {
        this.loaded = eventInitDict.loaded;
      }
      if (eventInitDict?.total !== undefined) {
        this.total = eventInitDict.total;
      }
      if (eventInitDict?.lengthComputable !== undefined) {
        this.lengthComputable = eventInitDict.lengthComputable;
      }
    }
  };

const eventTypes = [
  "abort",
  "error",
  "load",
  "loadend",
  "loadstart",
  "progress",
  "timeout",
  "readystatechange",
] as const;

function isXhrEvent(type: string): type is (typeof eventTypes)[number] {
  return eventTypes.includes(type as any);
}

class XMLHttpRequestEventTarget
  extends EventTarget
  implements globalThis.XMLHttpRequestEventTarget
{
  onabort: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onerror: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onload: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onloadend: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onloadstart: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onprogress: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  ontimeout: ((this: EventTarget, ev: ProgressEvent) => any) | null = null;
  onreadystatechange: ((this: EventTarget, ev: Event) => any) | null = null;

  dispatchEvent(event: Event): boolean {
    if (isXhrEvent(event.type) && this[`on${event.type}`]) {
      this[`on${event.type}`]!(event as any);
    }

    return super.dispatchEvent(event);
  }
}

export class XMLHttpRequestUpload
  extends XMLHttpRequestEventTarget
  implements globalThis.XMLHttpRequestUpload {}

/**
 * A completely cursed implementation of XMLHttpRequest that uses fetch instead
 * of the browser's native XMLHttpRequest implementation.
 */
export class XMLHttpRequest
  extends XMLHttpRequestEventTarget
  implements globalThis.XMLHttpRequest
{
  static UNSENT = 0 as const;
  static OPENED = 1 as const;
  static HEADERS_RECEIVED = 2 as const;
  static LOADING = 3 as const;
  static DONE = 4 as const;

  UNSENT = XMLHttpRequest.UNSENT;
  OPENED = XMLHttpRequest.OPENED;
  HEADERS_RECEIVED = XMLHttpRequest.HEADERS_RECEIVED;
  LOADING = XMLHttpRequest.LOADING;
  DONE = XMLHttpRequest.DONE;

  private _readyState: number = XMLHttpRequest.UNSENT;
  private _status = 0;
  private _statusText = "";
  private _url = "";
  private _method?: string;
  private _headers: Record<string, string> = {};
  private _overrideMimeType?: string;
  private _responseType: XMLHttpRequestResponseType = "";
  private _responseText = "";
  private _responseBuffer: ArrayBuffer | null = null;
  private _responseBlob: Blob | null = null;
  private _responseDocument: Document | null = null;
  private _responseJSON: any | null = null;

  private _controller = new AbortController();
  private _responseHeaders: Record<string, string> = {};
  private _responseURL = "";
  private _responseChunks: Uint8Array[] = [];

  readonly upload = new XMLHttpRequestUpload();

  body?: Document | XMLHttpRequestBodyInit | null;
  withCredentials = false;
  timeout = 0;

  set responseType(value: XMLHttpRequestResponseType) {
    if (this._readyState >= XMLHttpRequest.HEADERS_RECEIVED) {
      throw new DOMException(
        `Failed to set the 'responseType' property on 'XMLHttpRequest': The response type cannot be set if the object's state is LOADING or DONE.`,
        "InvalidStateError"
      );
    }
    if (
      value === "" ||
      value === "text" ||
      value === "blob" ||
      value === "arraybuffer" ||
      value === "document" ||
      value === "json"
    ) {
      this._responseType = value;
    }
  }
  get responseType() {
    return this._responseType;
  }
  get readyState() {
    return this._readyState;
  }

  get status() {
    return this._status;
  }
  get statusText() {
    return this._statusText;
  }

  get responseURL() {
    return this._responseURL;
  }

  get responseXML() {
    if (this._responseType !== "" && this._responseType !== "document") {
      throw new DOMException(
        `Failed to read the 'responseXML' property from 'XMLHttpRequest': The value is only accessible if the object's 'responseType' is '' or 'document' (was '${this._responseType}').`,
        "InvalidStateError"
      );
    }
    return this._responseDocument;
  }

  get responseText() {
    if (this._responseType !== "" && this._responseType !== "text") {
      throw new DOMException(
        `Failed to read the 'responseText' property from 'XMLHttpRequest': The value is only accessible if the object's 'responseType' is '' or 'text' (was '${this._responseType}').`,
        "InvalidStateError"
      );
    }
    return this._responseText;
  }

  get response() {
    if (this.readyState < XMLHttpRequest.DONE) {
      return null;
    }

    switch (this._responseType) {
      case "":
      case "text":
        return this._responseText;
      case "blob":
        return this._responseBlob;
      case "arraybuffer":
        return this._responseBuffer;
      case "json":
        return this._responseJSON;
      case "document":
        return this._responseDocument;
    }
  }

  overrideMimeType(mimeType: string) {
    this._overrideMimeType = mimeType;
  }

  open(method: string, url: string | URL): void;
  open(
    method: string,
    url: string | URL,
    async: boolean,
    username?: string | null,
    password?: string | null
  ): void;
  open(
    method: string,
    url: string | URL,
    async: boolean = true,
    username?: string | null,
    password?: string | null
  ) {
    if (async === false) {
      throw new Error("Sync XMLHttpRequest is not supported");
    }

    if (username || password) {
      if (typeof url === "string") {
        url = new URL(url);
      }

      if (username) {
        url.username = username;
      }
      if (password) {
        url.password = password;
      }
    }

    this._method = method;
    this._url = String(url);
    this._readyState = XMLHttpRequest.OPENED;
    this.dispatchEvent(
      new Event("readystatechange", {
        bubbles: false,
        cancelable: false,
      })
    );
  }

  setRequestHeader(header: string, value: string) {
    this._headers[header] = value;
  }

  send(body?: Document | XMLHttpRequestBodyInit | null) {
    this.body = body;
    this._readyState = XMLHttpRequest.HEADERS_RECEIVED;
    this.dispatchEvent(
      new Event("readystatechange", {
        bubbles: false,
        cancelable: false,
      })
    );

    const headers = new Headers(this._headers);

    if (!headers.has("Accept")) {
      switch (this._responseType) {
        case "":
        case "blob":
        case "arraybuffer":
        case "text":
          headers.set("Accept", "*/*");
          break;
        case "json":
          headers.set("Accept", "application/json, text/javascript");
          break;
        case "document":
          headers.set("Content-Type", "text/html");
          break;
      }
    }

    let abortTimeout: ReturnType<typeof setTimeout> | undefined;

    if (this.timeout > 0) {
      abortTimeout = setTimeout(() => {
        this._controller.abort();
      }, this.timeout);
    }

    const uploadTotal =
      body instanceof Blob ? body.size : body?.toString().length;

    const uploadProgressInit: ProgressEventInit & { loaded: number } = {
      lengthComputable: uploadTotal !== undefined,
      loaded: 0,
      total: uploadTotal,
      bubbles: false,
      cancelable: false,
    };

    this.upload.dispatchEvent(
      new ProgressEvent("loadstart", uploadProgressInit)
    );

    const downloadProgressInit: ProgressEventInit & { loaded: number } = {
      lengthComputable: false,
      loaded: 0,
      total: undefined,
      bubbles: false,
      cancelable: false,
    };

    let fetchBody: BodyInit | null | undefined;

    if (body instanceof Document) {
      fetchBody = new XMLSerializer().serializeToString(body);
    } else {
      // This would be the place to implement upload progress tracking by
      // converting the body to a stream and instrumenting as chunks are read
      fetchBody = body;
    }

    fetch(this._url, {
      method: this._method,
      headers,
      body: fetchBody,
      credentials: this.withCredentials ? "include" : "omit",
      signal: this._controller.signal,
    })
      .then((response) => {
        uploadProgressInit.loaded = uploadTotal || 0;

        this.upload.dispatchEvent(
          new ProgressEvent("load", uploadProgressInit)
        );
        this.upload.dispatchEvent(
          new ProgressEvent("loadend", uploadProgressInit)
        );

        let resolve: (value: void) => void;
        let reject: (reason: any) => void;

        const promise = new Promise<void>((res, rej) => {
          resolve = res;
          reject = rej;
        });

        this._status = response.status;
        this._statusText = response.statusText;
        this._readyState = XMLHttpRequest.LOADING;
        this.dispatchEvent(
          new Event("readystatechange", {
            bubbles: false,
            cancelable: false,
          })
        );

        for (const [key, value] of response.headers.entries()) {
          this._responseHeaders[key] = value;
        }

        this._responseURL = response.url;

        if (!response.body) {
          return;
        }

        const contentLength = response.headers.get("Content-Length");
        if (contentLength) {
          downloadProgressInit.total = Number.parseInt(contentLength, 10);
          downloadProgressInit.lengthComputable = true;
        }

        const reader = response.body.getReader();

        const decoder = new TextDecoder();

        const handleData = async (
          result: ReadableStreamReadResult<Uint8Array>
        ) => {
          if (result.done) {
            clearTimeout(abortTimeout);

            switch (this.responseType) {
              case "json":
                try {
                  this._responseJSON = JSON.parse(this._responseText);
                } catch {
                  // ignore
                }
                break;
              case "document":
                this._responseDocument = new DOMParser().parseFromString(
                  this._responseText,
                  (this._overrideMimeType ??
                    response.headers.get("Content-Type") ??
                    "text/html") as DOMParserSupportedType
                );
                break;
              case "blob":
                {
                  this._responseBlob = new Blob(this._responseChunks, {
                    type:
                      this._overrideMimeType ??
                      response.headers.get("Content-Type") ??
                      undefined,
                  });
                }
                break;
              case "arraybuffer":
                {
                  let length = 0;

                  this._responseChunks.forEach((chunk) => {
                    length += chunk.length;
                  });

                  const buffer = new ArrayBuffer(length);
                  const view = new Uint8Array(buffer);
                  let offset = 0;
                  this._responseChunks.forEach((chunk) => {
                    view.set(chunk, offset);
                    offset += chunk.byteLength;
                  });
                  this._responseBuffer = buffer;
                }
                break;
            }

            this._readyState = XMLHttpRequest.DONE;
            this.dispatchEvent(
              new Event("readystatechange", {
                bubbles: false,
                cancelable: false,
              })
            );
            this.dispatchEvent(new ProgressEvent("load", downloadProgressInit));
            this.dispatchEvent(
              new ProgressEvent("loadend", downloadProgressInit)
            );

            return;
          }

          if (result.value === undefined) {
            clearTimeout(abortTimeout);
            this._readyState = XMLHttpRequest.DONE;
            this.dispatchEvent(
              new Event("readystatechange", {
                bubbles: false,
                cancelable: false,
              })
            );
            this.dispatchEvent(
              new ProgressEvent("abort", downloadProgressInit)
            );
            this.dispatchEvent(
              new ProgressEvent("error", downloadProgressInit)
            );
            resolve!();
            return;
          }

          downloadProgressInit.loaded += result.value.length;

          this._responseText += decoder.decode(result.value, { stream: true });

          this._responseChunks.push(result.value);

          this.dispatchEvent(
            new ProgressEvent("progress", downloadProgressInit)
          );

          reader.read().then(handleData, reject!);
        };

        reader.read().then(handleData, reject!);

        return promise;
      })
      .catch((error) => {
        this._readyState = XMLHttpRequest.DONE;
        this.dispatchEvent(
          new Event("readystatechange", {
            bubbles: false,
            cancelable: false,
          })
        );

        if (error.name === "AbortError") {
          this.dispatchEvent(
            new ProgressEvent("timeout", downloadProgressInit)
          );
          this.upload.dispatchEvent(
            new ProgressEvent("timeout", downloadProgressInit)
          );
        } else {
          this.dispatchEvent(new ProgressEvent("error", downloadProgressInit));
          this.upload.dispatchEvent(
            new ProgressEvent("error", downloadProgressInit)
          );
          this.dispatchEvent(
            new ProgressEvent("loadend", downloadProgressInit)
          );
          this.upload.dispatchEvent(
            new ProgressEvent("loadend", downloadProgressInit)
          );
        }
      });
  }

  abort(): void {
    this._controller.abort();
  }

  getResponseHeader(header: string) {
    return this._responseHeaders[header] || null;
  }

  getAllResponseHeaders(): string {
    return Object.entries(this._responseHeaders)
      .map(([key, value]) => `${key}: ${value}`)
      .join("\r\n");
  }
}
